import React, { FC } from 'react';
import { FocusedFieldProps } from '../../types';
import { Controller, useFormContext } from 'react-hook-form';
import { FORM_FIELDS, LABELS } from '../../../../../constants';
import { DateInput, PhoneInput, SelectInput, TextInput } from '../../../../landing.common';
import { FormAddressSuggest } from '../../../ui/suggest/address';
import { Checkbox } from '@sravni/react-design-system';
import {
    additionalPhoneOwnerOptions,
    dependentsOptions,
    educationOptions,
    familyStatusOptions,
} from '../../../../../helpers/form.new/sources';
import { FullNameSuggest } from '../../../ui/suggest/full_name';
import { RadioField } from '../../components/form/components/focused.sheet';
import { FocusedFieldWithAlert } from '../common';

export const RegistrationStreetField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.REGISTRATION_STREET}
            render={({ field: { onBlur, ...field } }) => (
                <FormAddressSuggest
                    input={{
                        ...field,
                        label: LABELS[FORM_FIELDS.REGISTRATION_STREET],
                        errorMessage: errors[FORM_FIELDS.REGISTRATION_STREET]?.message,
                        ...props,
                    }}
                    onChange={field.onChange}
                    onBlur={onBlur}
                    toBound="house"
                />
            )}
        />
    );
};

export const RegistrationApartmentField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.REGISTRATION_APARTMENT}
            render={({ field }) => (
                <TextInput
                    {...field}
                    label="Квартира"
                    errorMessage={errors[FORM_FIELDS.REGISTRATION_APARTMENT]?.message}
                    {...props}
                />
            )}
        />
    );
};

export const RegistrationDateField: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <FocusedFieldWithAlert
            showAlert={isShowingOnSheet}
            field={
                <Controller
                    control={control}
                    name={FORM_FIELDS.REGISTRATION_DATE}
                    render={({ field }) => (
                        <DateInput
                            {...field}
                            label={LABELS[FORM_FIELDS.REGISTRATION_DATE]}
                            errorMessage={errors[FORM_FIELDS.REGISTRATION_DATE]?.message}
                            {...props}
                        />
                    )}
                />
            }
            alert="Введите дату регистрации по адресу прописки"
        />
    );
};

export const ResidenceSameRegistrationField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        getValues,
        setValue,
        trigger,
        formState: { dirtyFields: _ },
    } = form;

    const copyRegistrationAddress = () => {
        const [street, apartment] = getValues([FORM_FIELDS.REGISTRATION_STREET, FORM_FIELDS.REGISTRATION_APARTMENT]);
        setValue(FORM_FIELDS.RESIDENCE_STREET, street);
        setValue(FORM_FIELDS.RESIDENCE_APARTMENT, apartment);
        trigger(FORM_FIELDS.RESIDENCE_STREET);
    };

    const onChangeAddressTheSame = (
        onFieldChange: (...events: any[]) => void,
    ): React.FormEventHandler<HTMLInputElement> => {
        return (event) => {
            const {
                currentTarget: { checked },
            } = event;

            if (checked) {
                copyRegistrationAddress();
            }
            onFieldChange(event);
        };
    };

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.RESIDENCE_SAME_REGISTRATION}
            render={({ field: { value, ...field } }) => (
                <Checkbox checked={value} {...field} onChange={onChangeAddressTheSame(field.onChange)}>
                    Адрес фактического проживания совпадает с адресом регистрации
                </Checkbox>
            )}
        />
    );
};

export const ResidenceStreetField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.RESIDENCE_STREET}
            render={({ field: { onBlur, ...field } }) => (
                <FormAddressSuggest
                    input={{
                        ...field,
                        label: LABELS[FORM_FIELDS.RESIDENCE_STREET],
                        errorMessage: errors[FORM_FIELDS.RESIDENCE_STREET]?.message,
                        ...props,
                    }}
                    onChange={field.onChange}
                    onBlur={onBlur}
                    toBound="house"
                />
            )}
        />
    );
};

export const ResidenceApartmentField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.RESIDENCE_APARTMENT}
            render={({ field }) => (
                <TextInput
                    {...field}
                    label="Квартира"
                    errorMessage={errors[FORM_FIELDS.RESIDENCE_APARTMENT]?.message}
                    {...props}
                />
            )}
        />
    );
};

export const EducationField: FC<FocusedFieldProps> = (props) => {
    const { isShowingOnSheet, ...other } = props;
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    if (isShowingOnSheet) {
        return <RadioField options={educationOptions} fieldName={FORM_FIELDS.EDUCATION} form={form} />;
    }

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.EDUCATION}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    label={LABELS[FORM_FIELDS.EDUCATION]}
                    withCrossIcon={false}
                    options={educationOptions}
                    errorMessage={errors[FORM_FIELDS.EDUCATION]?.message}
                    {...other}
                />
            )}
        />
    );
};

export const FamilyStatusField: FC<FocusedFieldProps> = (props) => {
    const { isShowingOnSheet, ...other } = props;
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    if (isShowingOnSheet) {
        return <RadioField options={familyStatusOptions} fieldName={FORM_FIELDS.FAMILY_STATUS} form={form} />;
    }

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.FAMILY_STATUS}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    label={LABELS[FORM_FIELDS.FAMILY_STATUS]}
                    withCrossIcon={false}
                    options={familyStatusOptions}
                    errorMessage={errors[FORM_FIELDS.FAMILY_STATUS]?.message}
                    {...other}
                />
            )}
        />
    );
};

export const NumberOfDependentsField: FC<FocusedFieldProps> = (props) => {
    const { isShowingOnSheet, ...other } = props;
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    if (isShowingOnSheet) {
        return <RadioField options={dependentsOptions} fieldName={FORM_FIELDS.NUMBER_OF_DEPENDENTS} form={form} />;
    }

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.NUMBER_OF_DEPENDENTS}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    label={LABELS[FORM_FIELDS.NUMBER_OF_DEPENDENTS]}
                    options={dependentsOptions}
                    errorMessage={errors[FORM_FIELDS.NUMBER_OF_DEPENDENTS]?.message}
                    {...other}
                />
            )}
        />
    );
};

export const AdditionalPhoneField: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <FocusedFieldWithAlert
            showAlert={isShowingOnSheet}
            field={
                <Controller
                    control={control}
                    name={FORM_FIELDS.ADDITIONAL_PHONE}
                    render={({ field }) => (
                        <PhoneInput
                            {...field}
                            label={LABELS[FORM_FIELDS.ADDITIONAL_PHONE]}
                            errorMessage={errors[FORM_FIELDS.ADDITIONAL_PHONE]?.message}
                            {...props}
                        />
                    )}
                />
            }
            alert="Номер телефона не должен совпадать с рабочим или с указанным ранее"
        />
    );
};

export const AdditionalPhoneOwnerField: FC<FocusedFieldProps> = (props) => {
    const { isShowingOnSheet, ...other } = props;
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    if (isShowingOnSheet) {
        return (
            <FocusedFieldWithAlert
                showAlert
                field={
                    <RadioField
                        options={additionalPhoneOwnerOptions}
                        fieldName={FORM_FIELDS.ADDITIONAL_PHONE_OWNER}
                        form={form}
                    />
                }
                alert="Дополнительный номер увеличивает шансы на одобрение кредита"
            />
        );
    }

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    label={LABELS[FORM_FIELDS.ADDITIONAL_PHONE_OWNER]}
                    withCrossIcon={false}
                    options={additionalPhoneOwnerOptions}
                    errorMessage={errors[FORM_FIELDS.ADDITIONAL_PHONE_OWNER]?.message}
                    {...other}
                />
            )}
        />
    );
};

export const AdditionalPhoneOwnerFioField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        setValue,
        formState: { errors, dirtyFields: _ },
    } = form;

    const onSelectFullName: React.ComponentProps<typeof FullNameSuggest>['onSelect'] = (option) => {
        setValue(FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO, option.result);
    };

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO}
            render={({ field: { onBlur, ...field } }) => (
                <FullNameSuggest
                    input={{
                        ...field,
                        label: LABELS[FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO],
                        errorMessage: errors[FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO]?.message,
                        ...props,
                    }}
                    onSelect={onSelectFullName}
                    onChange={field.onChange}
                    onBlur={onBlur}
                />
            )}
        />
    );
};

export const EmailField: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <FocusedFieldWithAlert
            showAlert={isShowingOnSheet}
            field={
                <Controller
                    control={control}
                    name={FORM_FIELDS.EMAIL}
                    render={({ field }) => (
                        <TextInput
                            {...field}
                            type="email"
                            label={LABELS[FORM_FIELDS.EMAIL]}
                            errorMessage={errors[FORM_FIELDS.EMAIL]?.message as string}
                            {...props}
                        />
                    )}
                />
            }
            alert="На эту почту мы отправим решение по заявке"
        />
    );
};
